import { createContext, PropsWithChildren, useContext } from 'react';
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'dens... Remove this comment to see the full error message
import { DBSCAN } from 'density-clustering';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import filter from 'lodash/fp/filter';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/fp/isEqual';
import some from 'lodash/fp/some';
import { HotSpot, HotSpotItem } from './dataContext';
import { useScaledDataContext } from './scaledData';

export type Cluster<T extends HotSpotItem> = HotSpot<T>[];

const clusterDistance = 50;
const noop = () => {};

const maxFrameRate = 30;
const frameTime = Math.round(1000 / maxFrameRate);

const decimalPlaces = 5;

const clusterer = new DBSCAN();

function findCluster<T extends HotSpotItem>(clusters: Cluster<T>[], point: HotSpot<T> | undefined) {
   const isPoint = (other: HotSpot<T>) => isEqual(point, other);

   const matchingCluster = clusters.find(cluster => some(isPoint, cluster));

   return matchingCluster;
}

function findPoint<T extends HotSpotItem>(data: HotSpot<T>[], id: HotSpot<T>['id']) {
   return data.find(point => point.id === id);
}

function getOtherPoints<T extends HotSpotItem>(point: HotSpot<T>, collection: Cluster<T>) {
   const isPoint = (other: HotSpot<T>) => !isEqual(point, other);
   const otherPoints = filter(isPoint, collection);
   return otherPoints;
}

function getDeclusterZoomFactorForPoint<T extends HotSpotItem>(
   distance: number,
   point: HotSpot<T>,
   otherPoints: HotSpot<T>[]
) {
   const zoomFactors = otherPoints.map(otherPoint =>
      calcDeclusterZoomFactor(distance, point, otherPoint)
   );

   return Math.max(...zoomFactors);
}

function getDeclusterZoomFactors<T extends HotSpotItem>(distance: number, points: Cluster<T>) {
   return points.map(point => {
      const otherPoints = getOtherPoints(point, points);
      return getDeclusterZoomFactorForPoint(distance, point, otherPoints);
   });
}

function getFullDeclusterZoomFactor<T extends HotSpotItem>(distance: number, points: Cluster<T>) {
   const zoomFactors = getDeclusterZoomFactors(distance, points);

   const zoomFactor = Math.max(...zoomFactors);
   return roundUp(zoomFactor, decimalPlaces);
}

function getDeclusterZoomFactor<T extends HotSpotItem>(distance: number, points: Cluster<T>) {
   const zoomFactors = getDeclusterZoomFactors(distance, points);

   const zoomFactor = Math.min(...zoomFactors);
   return roundUp(zoomFactor, decimalPlaces);
}

const roundUp = (num: number, decimalPlaces: number) => {
   const precision = Math.pow(10, decimalPlaces);
   const epsilon = Number.EPSILON || 0;
   return Math.ceil((num + epsilon) * precision) / precision;
};
type Scaled = {
   scaledX?: number;
   scaledY?: number;
};

function getPoint<T extends Scaled>({ scaledX, scaledY }: T) {
   return { x: scaledX, y: scaledY };
}

function calcDeclusterZoomFactor<T extends HotSpotItem>(
   distance: number,
   dataA: HotSpot<T>,
   dataX: HotSpot<T>
) {
   const { x: a, y: b } = getPoint(dataA);
   const { x, y } = getPoint(dataX);
   // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
   const squaredA = Math.pow(a, 2);
   // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
   const squaredB = Math.pow(b, 2);
   // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
   const squaredX = Math.pow(x, 2);
   // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
   const squaredY = Math.pow(y, 2);
   // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
   const factoredX = 2 * a * x;
   // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
   const factoredY = 2 * b * y;
   const zoomFactor =
      distance / Math.sqrt(squaredA - factoredX + squaredB - factoredY + squaredX + squaredY);
   return zoomFactor;
}

type Point = { x: number; y: number };
type PointAsArr = [number, number];

const toOpticsFormat = ({ x, y }: Point): PointAsArr => [x, y];

function getOpticPoints<T extends HotSpotItem>(data: HotSpot<T>[]) {
   // @ts-expect-error TS(2345) FIXME: Argument of type '({ x, y }: Point) => PointAsArr'... Remove this comment to see the full error message
   return data.map(hotSpot => getPoint(hotSpot)).map(toOpticsFormat);
}

const runClusterer = (opticPoints: PointAsArr[], clusterDistance: number): Array<number[]> =>
   clusterer.run(opticPoints, clusterDistance);

function getClusters<T extends HotSpotItem>(data: HotSpot<T>[]): Cluster<T>[] {
   const opticPoints = getOpticPoints(data);

   const clusters = runClusterer(opticPoints, clusterDistance).map(cluster =>
      cluster.map(index => data[index])
   );

   const clustedDatapoints = flatten(clusters);
   const nonClusteredPoints = difference(data, clustedDatapoints);

   const clustersWithSingles = nonClusteredPoints.map(datapoint => [datapoint]).concat(clusters);

   return clustersWithSingles;
}

const clusteredDataDefaults = {
   data: [] as HotSpot<HotSpotItem>[],
   clusters: [] as Cluster<HotSpotItem>[],
   getClusterById: noop as unknown as (_id: string) => Cluster<HotSpotItem>,
   getDatapointById: noop as unknown as (_id: string) => HotSpot<HotSpotItem>,
   getDeclusterZoomFactor: noop as unknown as (_cluster: Cluster<HotSpotItem>) => number,
   getFullDeclusterZoomFactor: noop as unknown as (_cluster: Cluster<HotSpotItem>) => number,
};

const ClusteredDataContext = createContext(clusteredDataDefaults);

export type ClusteredDataContextType<T extends HotSpotItem> = {
   data: HotSpot<T>[];
   clusters: Cluster<T>[];
   getClusterById: (_id: string) => Cluster<T>;
   getDatapointById: (_id: string) => HotSpot<T>;
   getDeclusterZoomFactor: (_cluster: Cluster<T>) => number;
   getFullDeclusterZoomFactor: (_cluster: Cluster<T>) => number;
};

export function useClusteredDataContext<T extends HotSpotItem>() {
   const context = useContext(ClusteredDataContext);
   // @ts-expect-error TS(2352) FIXME: Conversion of type '{ data: HotSpot<HotSpotItem>[]... Remove this comment to see the full error message
   return context as ClusteredDataContextType<T>;
}

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export function ClusteredDataProvider({ children }: PropsWithChildren<{}>) {
   const scaledDataContext = useScaledDataContext();
   const data = scaledDataContext.data;

   const deboucedGetCluster = debounce(() => getClusters(data), frameTime, { leading: true });

   const clusters = deboucedGetCluster();

   function getFullZoomFactor<T extends HotSpotItem>(cluster: Cluster<T>) {
      return getFullDeclusterZoomFactor(clusterDistance, cluster);
   }

   function getZoomFactor<T extends HotSpotItem>(cluster: Cluster<T>) {
      return getDeclusterZoomFactor(clusterDistance, cluster);
   }

   const getClusterById = (id: string) => findCluster(clusters, findPoint(data, id));
   const getDatapointById = (id: string) => findPoint(data, id);

   const clusteredDataContext = {
      data,
      clusters,
      getClusterById,
      getDatapointById,
      getDeclusterZoomFactor: getZoomFactor,
      getFullDeclusterZoomFactor: getFullZoomFactor,
   };

   return (
      // @ts-expect-error TS(2322) FIXME: Type '{ data: ScaledHotSpot<HotSpotItem>[]; cluste... Remove this comment to see the full error message
      <ClusteredDataContext.Provider value={clusteredDataContext}>
         {children}
      </ClusteredDataContext.Provider>
   );
}
